import { setup } from 'xstate';

import * as Actions from './setup/actions';
import * as Actors from './setup/actors';
import * as Guards from './setup/guards';

import type {
  IdMeVerificationChildren,
  IdMeVerificationContext,
  IdMeVerificationEvent,
} from './setup';

// ─── Setup ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as IdMeVerificationContext,
    events: {} as IdMeVerificationEvent,
    children: {} as IdMeVerificationChildren,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const idMeVerificationMachine = model.createMachine({
  id: 'idme-verification',
  initial: 'setup',
  context: {
    request: undefined,
    authorizationCode: undefined,
    verifiedGroups: [],
    hasIdMeSession: false,
  },
  states: {
    setup: {
      initial: 'loading-verified-groups',
      states: {
        'loading-verified-groups': {
          invoke: {
            id: 'fetchVerifiedGroups',
            src: 'fetchVerifiedGroups',
            onDone: {
              target: 'routing',
              actions: [
                {
                  type: 'assignVerifiedGroups',
                  params: ({ event }) => ({ verifiedGroups: event.output }),
                },
                {
                  type: 'trackVerifiedGroups',
                  params: ({ event }) => ({ verifiedGroups: event.output }),
                },
              ],
            },
            onError: {
              target: 'failed',
              actions: {
                type: 'logVerifiedGroupsFailure',
                params: ({ event }) => ({ error: event.error }),
              },
            },
          },
        },
        failed: {
          on: {
            RETRY: 'loading-verified-groups',
          },
        },
        routing: {
          always: [
            {
              guard: 'checkIfAnyGroupActive',
              target: '#verified',
            },
            {
              target: '#ready',
            },
          ],
        },
      },
    },
    verified: {
      id: 'verified',
    },
    ready: {
      id: 'ready',
      initial: 'idle',

      // NOTE: Prepared ahead: Safari blocks a popup opened after an await instead of within the tap.
      invoke: {
        id: 'prepareRequest',
        src: 'createAuthorizationRequest',
        onDone: {
          actions: {
            type: 'assignRequest',
            params: ({ event }) => ({ request: event.output }),
          },
        },
        onError: {
          target: '.failed',
          actions: {
            type: 'logRequestFailure',
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
      on: {
        VERIFY: {
          target: 'verification',
          actions: 'trackVerifyTapped',
        },
      },
      states: {
        idle: {},
        cancelled: {
          entry: 'showCancelledNotice',
        },
        failed: {
          entry: 'showFailedNotice',
        },
        'not-verified': {
          entry: 'showNotVerifiedNotice',
          on: {
            SIGN_OUT: {
              guard: 'checkIfIdMeSessionActive',
              target: '#signing-out',
            },
          },
        },
        'link-conflict': {
          on: {
            SIGN_OUT: {
              guard: 'checkIfIdMeSessionActive',
              target: '#signing-out',
            },
          },
        },
      },
    },
    verification: {
      initial: 'starting',
      exit: 'clearAuthorization',
      states: {
        starting: {
          always: [
            {
              guard: 'checkIfRequestPrepared',
              target: 'authorizing',
            },
            {
              target: 'creating-request',
            },
          ],
        },
        'creating-request': {
          invoke: {
            id: 'createRequest',
            src: 'createAuthorizationRequest',
            onDone: {
              target: 'authorizing',
              actions: {
                type: 'assignRequest',
                params: ({ event }) => ({ request: event.output }),
              },
            },
            onError: {
              target: '#ready.failed',
              actions: [
                {
                  type: 'logRequestFailure',
                  params: ({ event }) => ({ error: event.error }),
                },
                {
                  type: 'trackVerificationFailed',
                  params: { failureReason: 'authorization_request_failed' },
                },
              ],
            },
          },
        },
        authorizing: {
          entry: 'trackVerificationStarted',
          invoke: {
            id: 'promptAuthorization',
            src: 'promptAuthorization',
            input: ({ context }) => context.request,
          },
          on: {
            AUTHORIZATION_RESULT: [
              {
                guard: {
                  type: 'checkIfAuthorized',
                  params: ({ event }) => ({ result: event.result }),
                },
                target: 'verifying',
                actions: [
                  {
                    type: 'assignAuthorizationCode',
                    params: ({ event }) => ({ result: event.result }),
                  },
                  'assignIdMeSession',
                ],
              },
              {
                guard: {
                  type: 'checkIfAuthorizationCancelled',
                  params: ({ event }) => ({ result: event.result }),
                },
                target: '#ready.cancelled',
                actions: 'trackVerificationAbandoned',
              },
              {
                target: '#ready.failed',
                actions: [
                  {
                    type: 'logAuthorizationFailure',
                    params: ({ event }) => ({ result: event.result }),
                  },
                  {
                    type: 'trackAuthorizationFailure',
                    params: ({ event }) => ({ result: event.result }),
                  },
                ],
              },
            ],
          },
        },
        verifying: {
          invoke: {
            id: 'verifyGroup',
            src: 'verifyGroup',
            input: ({ context }) => ({
              request: context.request,
              code: context.authorizationCode,
            }),
            onDone: [
              {
                guard: {
                  type: 'checkIfVerified',
                  params: ({ event }) => ({ verificationResult: event.output }),
                },
                target: '#verified',
                actions: [
                  {
                    type: 'assignVerifiedGroupsFromResult',
                    params: ({ event }) => ({ verificationResult: event.output }),
                  },
                  {
                    type: 'trackVerificationSucceeded',
                    params: ({ event }) => ({ verificationResult: event.output }),
                  },
                ],
              },
              {
                guard: {
                  type: 'checkIfNotVerified',
                  params: ({ event }) => ({ verificationResult: event.output }),
                },
                target: '#ready.not-verified',
                actions: {
                  type: 'trackVerificationFailed',
                  params: { failureReason: 'not_verified' },
                },
              },
              {
                guard: {
                  type: 'checkIfLinkedToAnotherIdentity',
                  params: ({ event }) => ({ verificationResult: event.output }),
                },
                target: '#ready.link-conflict',
                actions: [
                  {
                    type: 'showLinkConflictNotice',
                    params: ({ event }) => ({ verificationResult: event.output }),
                  },
                  'trackAccountConflict',
                ],
              },
              {
                target: '#ready.link-conflict',
                actions: [
                  {
                    type: 'showLinkConflictNotice',
                    params: ({ event }) => ({ verificationResult: event.output }),
                  },
                  {
                    type: 'trackVerificationFailed',
                    params: { failureReason: 'identity_linked_to_another_account' },
                  },
                ],
              },
            ],
            onError: {
              target: '#ready.failed',
              actions: [
                {
                  type: 'logVerificationFailure',
                  params: ({ event }) => ({ error: event.error }),
                },
                {
                  type: 'trackVerificationFailed',
                  params: { failureReason: 'verification_request_failed' },
                },
              ],
            },
          },
        },
      },
    },
    'signing-out': {
      id: 'signing-out',
      invoke: {
        id: 'signOut',
        src: 'signOut',
        onDone: {
          target: 'ready',
          actions: {
            type: 'assignSignOutResult',
            params: ({ event }) => ({ signOutResult: event.output }),
          },
        },
        onError: {
          target: 'ready',
          actions: {
            type: 'logSignOutFailure',
            params: ({ event }) => ({ error: event.error }),
          },
        },
      },
    },
  },
});
